import { Dialog, DialogContent } from "@/components/ui/dialog";
import { StoreProjectAssigneesService } from "@/services/storeProjectAssignees";
import { memo, useCallback, useMemo } from "react";
import { StoreProjectAssigneeListItem } from "@/types/storeProjectAssignees";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { StoreProjectAssigneesDeleteDialogIdle } from "./StoreProjectAssigneesDeleteDialogIdle";
import {
  AsyncOperationProgress,
  OperationProgressItem,
  OperationProgressStatus,
} from "@/components/progress";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";
import { AsyncOperationItem } from "@/components/progress/AsyncOperationItem";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { useRowSelectionCount } from "@/components/data-table/hooks/useRowSelectionCount";

interface StoreProjectAssigneesDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleteSuccess?: () => void;
}

const StoreProjectAssigneesDeleteDialogComponent = ({
  open,
  onOpenChange,
  onDeleteSuccess,
}: StoreProjectAssigneesDeleteDialogProps) => {
  const { store } = useRowSelectionContext();
  const selectedCount = useRowSelectionCount();
  const { table } = useDataTableSettings<StoreProjectAssigneeListItem>();
  const tableData = useMemo(
    () => table.getRowModel().rows.map((row) => row.original),
    [table],
  );

  const getItemName = useCallback(
    (id: string) => {
      const item = tableData.find((item) => item.id === id);
      return item
        ? `${item.user_name || ""} - ${item.store_name || ""} - ${item.project_name || ""}`
        : id;
    },
    [tableData],
  );

  const renderCompletedItem = useCallback(
    (item: OperationProgressItem) => {
      return (
        <AsyncOperationItem
          label={getItemName(item.id)}
          success={item.success}
        />
      );
    },
    [getItemName],
  );

  const operationProgress = useAsyncOperationProgress();

  const {
    status,
    initialize,
    startProcessing,
    setCurrentItem,
    markItemProcessed,
    complete,
  } = operationProgress;

  const executeDelete = useCallback(
    async (ids: string[]) => {
      const items = ids.map((id) => ({
        id,
        label: getItemName(id),
      }));
      initialize(items);
      startProcessing();

      for (const id of ids) {
        // isCancelling is not memoized, but safe to access directly
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (operationProgress.isCancelling()) {
          break;
        }

        const itemLabel = getItemName(id);
        setCurrentItem(itemLabel);

        try {
          await StoreProjectAssigneesService.delete(id);
          markItemProcessed(id, true);
        } catch (error) {
          markItemProcessed(id, false);
        }
      }

      complete();
    },
    // operationProgress is intentionally omitted - isCancelling is not memoized but safe to access
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getItemName,
      initialize,
      startProcessing,
      setCurrentItem,
      markItemProcessed,
      complete,
    ],
  );

  const deleteMutation = useMutationWithErrorHandling({
    mutationFn: executeDelete,
    onError: complete,
  });

  const handleStartDelete = useCallback(() => {
    deleteMutation.mutate(Array.from(store.getSelection()));
  }, [deleteMutation, store]);

  const onCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const onReset = useCallback(() => {
    onDeleteSuccess?.();
  }, [onDeleteSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        {status === OperationProgressStatus.IDLE && (
          <StoreProjectAssigneesDeleteDialogIdle
            selectedIdsCount={selectedCount}
            onCancel={onCancel}
            onConfirm={handleStartDelete}
            isPending={deleteMutation.isPending}
          />
        )}
        <AsyncOperationProgress
          operationProgress={operationProgress}
          currentItemLabel={operationProgress.currentItemLabel}
          onClose={onReset}
          translationKey="storeProjectAssignees.deleteDialog.progress"
          renderCompletedItem={renderCompletedItem}
        />
      </DialogContent>
    </Dialog>
  );
};

export const StoreProjectAssigneesDeleteDialog = memo(
  StoreProjectAssigneesDeleteDialogComponent,
);
